import styles from '../index.module.scss';
import { FormText, FormPassword } from '@components/form';
import { Text } from '@components/text';
import { Button, small_height, utility } from '@phi/components';
import { PropType, defineComponent, h, ref } from 'vue';
import { LogInWithOnshape } from '../log-in-with-onshape';
import { set } from '@phi/util/vuetils';
import { ENV } from '@phi/config/frontend';
import { Email } from '@phi/newtype';
import { Flex } from '@phi/components/layout';
import { Option } from '@vaaas/fpts/option';
import { ErrorPrinter } from '../error-printer';
import { absorbed } from '@vaaas/fpts/dom';

export type FormData = {
    email: Email;
    password: string;
}

export const LoginForm = defineComponent({
    name: 'LoginForm',

    props: {
        env: { required: false, type: String, default: ENV },

        error: {
            required: false,
            type: null as any as PropType<Option<Error>>,
            default: undefined,
        },

        light: {
            required: false,
            type: Boolean,
            default: false,
        },
    },

    emits: {
        local: () => true,
        onshape: () => true,
        login: (x: FormData) => true,
        register: () => true,
        forgot: () => true,
    },

    setup(props, { emit }) {
        const dev = props.env === 'development';
        const email = ref('');
        const password = ref('');

        const formDataProvider = (): FormData => ({
            email: email.value as any as Email,
            password: password.value,
        });

        return () => h('form', {
            class: {
                [utility.margin_gap_3]: small_height(),
                [utility.margin_gap_5]: !small_height(),
                [styles.shake]: props.error !== undefined,
            },
            onSubmit: absorbed(() => emit('login', formDataProvider())),
        }, [
            h('header', [
                h(Text, { size: 'huge', class: styles.faded }, () => 'Log In'),
                h('div', [
                    h('span', { class: styles.faded }, 'Don\'t have an account yet? '),
                    h('a', {
                        href: '#',
                        class: [utility.underline, 'go-to-register-page'],
                        onClick: () => emit('register'),
                    }, 'Create Account'),
                ]),
            ]),

            h(Flex, {
                gap: 'tighter',
                direction: 'row',
                wrap: true,
                as: 'fieldset',
                equally: true,
            }, () => [
                LogInWithOnshape(props.light, () => emit('onshape')),
                dev && h(Button, {
                    class: [styles.height, 'local-login'],
                    block: true,
                    onClick: () => emit('local'),
                }, () => 'Log In Locally'),
            ]),

            h('fieldset', [
                h('label', { id: 'login-email' }, 'Email Address'),
                h(FormText, {
                    name: 'email',
                    id: 'login-email',
                    value: email.value,
                    'onUpdate:value': set(email),
                    type: 'email',
                    variant: 'outlined',
                    autocomplete: 'email',
                }),
            ]),

            h('fieldset', [
                h('label', {
                    class: [
                        utility.flex,
                        utility.space_between,
                    ],
                    for: 'login-password'
                }, [
                    'Password',
                    h('span', {
                        class: [
                            'forgot-label',
                            utility.underline,
                            utility.pointer,
                            styles.faded,
                        ],
                        onClick: () => emit('forgot'),
                    }, 'Forgot?'),
                ]),
                h(FormPassword, {
                    name: 'password',
                    id: 'login-password',
                    value: password.value,
                    'onUpdate:value': set(password),
                    variant: 'outlined',
                    autocomplete: 'current-password',
                }),
            ]),

            ErrorPrinter(props.error),

            h(Button, {
                type: 'submit',
                class: [ styles.height, 'log-in' ],
                variant: 'primary',
                block: true,
            }, () => h('span', { class: utility.bold }, 'Log In')),
        ]);
    }
});
